/**
 * ZureX92 — Extension Pages
 * All new feature pages in a single importable module.
 * Exports page components for: Intel Engine, Campaign Analytics,
 * Advanced Traps, OSINT+, Case Builder, Threat Intel, Domain Manager,
 * Automation, Integrations, Access Tokens, Live Dashboard.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE_URL || '';
const api = axios.create({ baseURL: API });
api.interceptors.request.use(c => { const t = localStorage.getItem('zx_t'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });
const E = e => e?.response?.data?.detail || e?.message || 'Error';

// ═══ SHARED UI PRIMITIVES ═══
const P = ({ title, sub, extra, children }) => (
  <div className="border border-[#222] rounded-xl bg-[#0a0a0a]">
    <div className="px-5 py-3 border-b border-[#1a1a1a] flex items-center justify-between gap-2">
      <div className="min-w-0"><h2 className="text-[14px] font-semibold text-white">{title}</h2>
        {sub && <p className="text-[12px] text-[#444] mt-0.5">{sub}</p>}</div>
      {extra && <div className="flex-shrink-0 flex gap-2 items-center">{extra}</div>}
    </div><div className="p-5">{children}</div>
  </div>
);
const SC = ({ l, v, c }) => (
  <div className={`border border-[#222] rounded-xl p-4 bg-[#0a0a0a] ${c || ''}`}>
    <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">{l}</p>
    <p className="text-[22px] font-light text-white">{v ?? 0}</p>
  </div>
);
const Bn = ({ children, v = 'primary', ...p }) => {
  const s = { primary: 'bg-white text-black hover:bg-gray-200', secondary: 'bg-[#111] text-white border border-[#333]', danger: 'bg-red-950 text-red-400 border border-red-900', ghost: 'bg-transparent text-[#888] hover:text-white' };
  return <button className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all disabled:opacity-30 ${s[v] || s.primary}`} {...p}>{children}</button>;
};
const Bd = ({ children, v = 'default' }) => {
  const s = { default: 'border-[#333] text-[#888]', success: 'border-emerald-900 text-emerald-400 bg-emerald-950', danger: 'border-red-900 text-red-400 bg-red-950', warning: 'border-amber-900 text-amber-400 bg-amber-950', blue: 'border-blue-900 text-blue-400 bg-blue-950', purple: 'border-purple-900 text-purple-400 bg-purple-950' };
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${s[v] || s.default}`}>{children}</span>;
};
const toast = (m) => { /* simplified */ };

// ═══════════════════════════════════════
// 1. INTEL ENGINE PAGE
// ═══════════════════════════════════════
export function IntelSearchPage() {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('auto');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('search');
  const [clusters, setClusters] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [correlationResult, setCorrelationResult] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [deviceDetail, setDeviceDetail] = useState(null);

  useEffect(() => { api.get('/api/v1/intel/alerts').then(r => setAlerts(r.data || [])).catch(() => {}); }, []);

  const search = async () => {
    if (!query || query.length < 3) return;
    setLoading(true);
    try { setResults((await api.post('/api/v1/intel/search', { query, type: searchType })).data); } catch (e) { alert(E(e)); }
    setLoading(false);
  };
  const correlate = async (id) => {
    try { setCorrelationResult((await api.get(`/api/v1/intel/correlate/${id}`)).data); setTab('correlation'); } catch (e) { alert(E(e)); }
  };
  const loadTimeline = async (id) => {
    try { setTimeline((await api.get(`/api/v1/intel/timeline/${id}`)).data); setTab('timeline'); } catch (e) { alert(E(e)); }
  };
  const loadDevice = async (cid) => {
    try { setDeviceDetail((await api.get(`/api/v1/intel/device/${cid}`)).data); setTab('device'); } catch (e) { alert(E(e)); }
  };
  const exportReport = async (id) => {
    try {
      const r = await api.get(`/api/v1/intel/export/${id}`, { responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
      a.download = `intel-${id}.html`; a.click();
    } catch (e) { alert(E(e)); }
  };

  const tabs = [{ id: 'search', l: 'Deep Search' }, { id: 'clusters', l: 'Fingerprint Clusters' }, { id: 'alerts', l: `Alerts (${alerts.filter(a => !a.is_read).length})` }, { id: 'correlation', l: 'Correlation' }, { id: 'timeline', l: 'Timeline' }, { id: 'device', l: 'Device Detail' }];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">Intelligence</p><h1 className="text-[22px] font-bold text-white">Intel Search Engine</h1></div>
        <div className="flex border border-[#222] rounded-lg overflow-hidden flex-wrap">
          {tabs.map(t => <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'clusters' && !clusters) api.get('/api/v1/intel/fingerprints').then(r => setClusters(r.data)).catch(() => {}); }}
            className={`px-3 py-1.5 text-[11px] ${tab === t.id ? 'bg-white text-black' : 'text-[#666] hover:text-white'}`}>{t.l}</button>)}
        </div>
      </div>

      {tab === 'search' && <P title="Advanced Search" sub="Search by fingerprint, IP, user-agent, or raw data">
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            {['auto', 'fingerprint', 'ip', 'useragent', 'raw'].map(v => <button key={v} onClick={() => setSearchType(v)} className={`px-3 py-1 rounded text-[11px] border ${searchType === v ? 'border-white text-white' : 'border-[#222] text-[#666]'}`}>{v}</button>)}
          </div>
          <div className="flex gap-3">
            <input className="flex-1 bg-[#0a0a0a] border border-[#222] px-4 py-2.5 text-white rounded-lg text-[13px] outline-none font-mono" placeholder="Search intelligence data..." value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} />
            <Bn onClick={search} disabled={loading}>{loading ? 'Searching...' : 'Search'}</Bn>
          </div>
        </div>
      </P>}

      {tab === 'search' && results && <P title={`Results: ${results.total}`} extra={<Bd>{results.type}</Bd>}>
        {results.results?.length === 0 ? <p className="text-[#555] py-4">No matches</p> : <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {results.results?.map(r => <div key={r.id} className="border border-[#1a1a1a] rounded-lg p-3 hover:border-[#333]">
            <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2"><span className="text-white font-mono text-[13px]">{r.public_ip}</span><Bd>{r.campaign || r.trap_id}</Bd></div>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => correlate(r.id)} className="text-[10px] px-2 py-0.5 rounded border border-amber-900 text-amber-400 hover:bg-amber-950">Correlate</button>
                <button onClick={() => loadTimeline(r.id)} className="text-[10px] px-2 py-0.5 rounded border border-blue-900 text-blue-400 hover:bg-blue-950">Timeline</button>
                <button onClick={() => exportReport(r.id)} className="text-[10px] px-2 py-0.5 rounded border border-emerald-900 text-emerald-400 hover:bg-emerald-950">Export</button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-[11px]">
              {r.canvas_hash && <div><span className="text-[#555]">Canvas: </span><span className="text-[#888] font-mono">{String(r.canvas_hash).substring(0, 16)}...</span></div>}
              {r.platform && <div><span className="text-[#555]">Platform: </span><span className="text-[#888]">{r.platform}</span></div>}
            </div>
          </div>)}
        </div>}
      </P>}

      {tab === 'alerts' && <P title="Intel Alerts"><div className="space-y-2 max-h-[500px] overflow-y-auto">
        {alerts.length === 0 ? <p className="text-[#555] py-8 text-center">No alerts yet</p> : alerts.map(a => <div key={a.id} className={`border rounded-lg p-3 ${a.is_read ? 'border-[#111] opacity-40' : 'border-amber-900 bg-amber-950/10'}`}>
          <div className="flex justify-between"><Bd v={a.alert_type === 'cross_trap' ? 'danger' : 'warning'}>{a.alert_type}</Bd>
            {!a.is_read && <button onClick={() => { api.put(`/api/v1/intel/alerts/${a.id}/read`).catch(() => {}); setAlerts(p => p.map(x => x.id === a.id ? { ...x, is_read: true } : x)); }} className="text-[10px] text-[#555]">Mark read</button>}
          </div><p className="text-[12px] text-[#ccc] mt-2">{a.message}</p>
        </div>)}
      </div></P>}

      {tab === 'clusters' && <P title="Fingerprint Clusters" extra={<Bn v="ghost" onClick={() => api.get('/api/v1/intel/fingerprints').then(r => setClusters(r.data)).catch(() => {})}>Refresh</Bn>}>
        {!clusters ? <p className="text-[#555]">Loading...</p> : <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3 mb-4"><SC l="Unique Devices" v={clusters.total_fingerprints} /><SC l="Cross-Trap" v={clusters.cross_trap_count} c={clusters.cross_trap_count > 0 ? 'border-red-900' : ''} /><SC l="Clusters" v={clusters.clusters?.length || 0} /></div>
          {clusters.clusters?.map((c, i) => <div key={i} className={`border rounded-lg p-3 ${c.cross_trap ? 'border-red-900 bg-red-950/10' : 'border-[#1a1a1a]'}`}>
            <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">{c.cross_trap && <Bd v="danger">CROSS-TRAP</Bd>}<span className="text-white text-[12px] font-mono">{c.canvas_hash?.substring(0, 20)}...</span></div>
              <div className="flex gap-2"><Bd>{c.total_captures} captures</Bd><Bd>{c.unique_ips} IPs</Bd></div>
            </div>
          </div>)}
        </div>}
      </P>}

      {tab === 'correlation' && correlationResult && <P title="Correlation Results" extra={<Bd v={correlationResult.total_matches > 0 ? 'danger' : 'success'}>{correlationResult.total_matches} matches</Bd>}>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">{correlationResult.matches?.map(m => <div key={m.id} className={`border rounded-lg p-3 ${!m.same_ip ? 'border-red-900 bg-red-950/10' : 'border-[#222]'}`}>
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${m.score >= 80 ? 'text-red-400 border-red-900 bg-red-950' : m.score >= 50 ? 'text-amber-400 border-amber-900 bg-amber-950' : 'text-blue-400 border-blue-900 bg-blue-950'}`}>{m.score}%</span>
              <span className="text-white font-mono text-[12px]">{m.public_ip}</span><Bd>{m.campaign || m.trap_id}</Bd></div>
            <span className="text-[10px] text-[#444]">{m.captured_at}</span>
          </div>
          <div className="flex gap-1 mt-2 flex-wrap">{m.matched_keys?.map(k => <span key={k} className="text-[9px] px-2 py-0.5 rounded bg-emerald-950 text-emerald-400 border border-emerald-900">{k}</span>)}</div>
        </div>)}</div>
      </P>}

      {tab === 'timeline' && timeline && <P title="Activity Timeline" sub={`${timeline.total_events || 0} events`}>
        <div className="relative pl-6 space-y-1 max-h-[500px] overflow-y-auto">
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-[#222]" />
          {timeline.timeline?.map((evt, i) => <div key={i} className="relative pl-4 py-2 hover:bg-[#060606] rounded-lg">
            <div className={`absolute left-0 top-3 w-[9px] h-[9px] rounded-full border-2 ${evt.is_vpn ? 'bg-amber-500 border-amber-700' : i === 0 ? 'bg-white border-white' : 'bg-[#333] border-[#555]'}`} />
            {evt.gap_label && <div className="text-[10px] text-[#444] -mt-1 mb-1">{evt.gap_label}</div>}
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div className="flex items-center gap-2"><span className="text-white font-mono text-[12px]">{evt.ip}</span><Bd>{evt.campaign || evt.trap_id}</Bd>{evt.is_vpn && <Bd v="warning">VPN</Bd>}</div>
              <span className="text-[10px] text-[#444]">{evt.timestamp}</span>
            </div>
          </div>)}
        </div>
      </P>}

      {tab === 'device' && deviceDetail && <P title="Device Profile" extra={<Bd v={deviceDetail.total > 1 ? 'danger' : 'default'}>{deviceDetail.total} captures</Bd>}>
        {deviceDetail.device && <div className="grid grid-cols-4 gap-3"><SC l="First Seen" v={deviceDetail.device.first_seen ? new Date(deviceDetail.device.first_seen).toLocaleDateString() : '—'} /><SC l="Last Seen" v={deviceDetail.device.last_seen ? new Date(deviceDetail.device.last_seen).toLocaleDateString() : '—'} /><SC l="Unique IPs" v={deviceDetail.device.ip_addresses?.length || 0} /><SC l="Traps" v={deviceDetail.device.trap_ids?.length || 0} /></div>}
      </P>}
    </div>
  );
}

// ═══════════════════════════════════════
// 2. CAMPAIGN ANALYTICS PAGE
// ═══════════════════════════════════════
export function CampaignPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [live, setLive] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => { api.get('/api/v1/campaigns/analytics').then(r => setCampaigns(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (tab === 'live') { const iv = setInterval(() => api.get('/api/v1/campaigns/live').then(r => setLive(r.data)).catch(() => {}), 5000); api.get('/api/v1/campaigns/live').then(r => setLive(r.data)).catch(() => {}); return () => clearInterval(iv); } }, [tab]);

  const loadAnalytics = async (tid) => {
    try { setAnalytics((await api.get(`/api/v1/campaigns/analytics/${tid}`)).data); setSelected(tid); setTab('detail'); } catch (e) { alert(E(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between"><div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">Analytics</p><h1 className="text-[22px] font-bold text-white">Campaign Analytics</h1></div>
        <div className="flex border border-[#222] rounded-lg overflow-hidden">
          {[{ id: 'overview', l: 'Overview' }, { id: 'live', l: 'Live Dashboard' }, { id: 'detail', l: 'Detail' }].map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-[11px] ${tab === t.id ? 'bg-white text-black' : 'text-[#666] hover:text-white'}`}>{t.l}</button>)}
        </div></div>

      {tab === 'overview' && <P title="All Campaigns"><div className="space-y-2">{campaigns.map(c => <div key={c.trap_id} onClick={() => loadAnalytics(c.trap_id)} className="border border-[#1a1a1a] rounded-lg p-3 cursor-pointer hover:border-[#333] flex justify-between items-center">
        <div><span className="text-white text-[13px] font-medium">{c.campaign_name || c.trap_id}</span><span className="text-[11px] text-[#444] ml-3">{c.template}</span></div>
        <div className="flex gap-3 items-center"><Bd>{c.capture_count || c.visit_count || 0} captures</Bd><span className="text-[10px] text-[#444]">{c.created_at && new Date(c.created_at).toLocaleDateString()}</span></div>
      </div>)}</div></P>}

      {tab === 'live' && live && <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3"><SC l="Captures (1h)" v={live.captures_1h} /><SC l="Captures (24h)" v={live.captures_24h} /><SC l="Active Traps" v={live.active_traps} /><SC l="Unread Alerts" v={live.unread_alerts} c={live.unread_alerts > 0 ? 'border-red-900' : ''} /></div>
        <P title="Recent Captures" sub="Auto-refreshing every 5s"><div className="space-y-1">{live.recent_captures?.map(c => <div key={c.id} className="flex justify-between py-2 border-b border-[#0a0a0a] text-[12px]">
          <div className="flex gap-2"><span className="text-white font-mono">{c.public_ip}</span><Bd>{c.campaign_name || c.trap_id}</Bd></div>
          <span className="text-[#444]">{c.captured_at && new Date(c.captured_at).toLocaleString()}</span>
        </div>)}</div></P>
      </div>}

      {tab === 'detail' && analytics && <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3"><SC l="Total Visits" v={analytics.total_visits} /><SC l="Unique Visitors" v={analytics.unique_visitors} /><SC l="VPN %" v={`${analytics.vpn_percentage}%`} /><SC l="First Visit" v={analytics.first_visit ? new Date(analytics.first_visit).toLocaleDateString() : '—'} /></div>
        <div className="grid grid-cols-2 gap-4">
          <P title="Country Distribution">{analytics.country_distribution?.slice(0, 10).map(c => <div key={c.country} className="flex justify-between py-1 border-b border-[#0a0a0a] text-[12px]"><span className="text-[#ccc]">{c.country}</span><span className="text-white font-medium">{c.count}</span></div>)}</P>
          <P title="Device Breakdown"><div className="grid grid-cols-2 gap-2"><SC l="Mobile" v={analytics.device_breakdown?.mobile} /><SC l="Desktop" v={analytics.device_breakdown?.desktop} /></div></P>
        </div>
        <P title="Daily Trend"><div className="flex items-end gap-1 h-[120px]">{analytics.daily_trend?.map((d, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1"><div className="bg-white rounded-sm w-full" style={{ height: `${Math.max(4, (d.count / Math.max(...analytics.daily_trend.map(x => x.count), 1)) * 100)}px` }} /><span className="text-[8px] text-[#444]">{d.date?.slice(5)}</span></div>)}</div></P>
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════
// 3. ADVANCED TRAPS PAGE
// ═══════════════════════════════════════
export function AdvancedTrapsPage() {
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState({ own: [], public: [] });
  const [selfDestruct, setSelfDestruct] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [fakeTemplates, setFakeTemplates] = useState([]);

  useEffect(() => {
    api.get('/api/v1/templates').then(r => setTemplates(r.data)).catch(() => {});
    api.get('/api/v1/self-destruct').then(r => setSelfDestruct(r.data || [])).catch(() => {});
    api.get('/api/v1/schedules').then(r => setSchedules(r.data || [])).catch(() => {});
    api.get('/api/v1/fake-pages/templates').then(r => setFakeTemplates(r.data || [])).catch(() => {});
  }, []);

  const tabs = [{ id: 'templates', l: 'Templates' }, { id: 'selfdestruct', l: 'Self-Destruct' }, { id: 'schedules', l: 'Schedules' }, { id: 'fakepages', l: 'Fake Pages' }, { id: 'geofence', l: 'Geo-Fence' }];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between"><div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">Advanced</p><h1 className="text-[22px] font-bold text-white">Advanced Trap System</h1></div>
        <div className="flex border border-[#222] rounded-lg overflow-hidden flex-wrap">{tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-[11px] ${tab === t.id ? 'bg-white text-black' : 'text-[#666] hover:text-white'}`}>{t.l}</button>)}</div>
      </div>

      {tab === 'templates' && <P title="Trap Templates Marketplace" sub="Pre-built and community templates">
        <h3 className="text-[12px] text-[#555] uppercase tracking-wider mb-2 mt-2">Your Templates</h3>
        {templates.own?.length === 0 ? <p className="text-[#444] text-[12px]">No templates yet</p> : templates.own?.map(t => <div key={t.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2"><div className="flex justify-between"><span className="text-white text-[13px]">{t.name}</span><Bd>{t.category}</Bd></div><p className="text-[11px] text-[#555] mt-1">{t.description}</p></div>)}
        <h3 className="text-[12px] text-[#555] uppercase tracking-wider mb-2 mt-4">Public Templates</h3>
        {templates.public?.map(t => <div key={t.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2"><div className="flex justify-between"><span className="text-white text-[13px]">{t.name}</span><div className="flex gap-2"><Bd>{t.use_count} uses</Bd><Bn v="ghost" onClick={() => api.post(`/api/v1/templates/${t.id}/use`).catch(() => {})}>Use</Bn></div></div></div>)}
      </P>}

      {tab === 'selfdestruct' && <P title="Self-Destruct Links" sub="Links that expire after N visits or time limit">
        {selfDestruct.map(s => <div key={s.id} className={`border rounded-lg p-3 mb-2 ${s.is_expired ? 'border-[#111] opacity-40' : 'border-amber-900'}`}>
          <div className="flex justify-between items-center"><span className="text-white font-mono text-[12px]">{s.trap_id}</span>
            <div className="flex gap-2"><Bd v={s.is_expired ? 'danger' : 'warning'}>{s.current_visits}/{s.max_visits} visits</Bd><Bd>{s.is_expired ? 'EXPIRED' : 'ACTIVE'}</Bd></div></div>
          <p className="text-[10px] text-[#444] mt-1">Expires: {s.expires_at ? new Date(s.expires_at).toLocaleString() : '—'}</p>
        </div>)}
      </P>}

      {tab === 'fakepages' && <P title="Fake Login Pages" sub="Generate convincing login pages for intelligence collection">
        <div className="grid grid-cols-3 gap-3">{fakeTemplates.map(t => <div key={t.id} className="border border-[#1a1a1a] rounded-lg p-4 cursor-pointer hover:border-[#333] text-center">
          <div className="text-[24px] mb-2">{t.logo ? '🔗' : '📄'}</div><p className="text-white text-[13px] font-medium">{t.title}</p>
          <p className="text-[10px] text-[#555] mt-1">{t.id}</p>
        </div>)}</div>
      </P>}

      {tab === 'schedules' && <P title="Scheduled Operations">{schedules.length === 0 ? <p className="text-[#555]">No scheduled operations</p> : schedules.map(s => <div key={s.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2 flex justify-between items-center">
        <div><span className="text-white text-[13px]">{s.action}</span><span className="text-[#444] text-[11px] ml-2">{s.trap_id}</span></div>
        <div className="flex gap-2"><Bd>{s.schedule_type}</Bd><Bd v={s.is_active ? 'success' : 'default'}>{s.is_active ? 'Active' : 'Off'}</Bd></div>
      </div>)}</P>}

      {tab === 'geofence' && <P title="Geo-Fencing Rules" sub="Restrict trap access by location or device type">
        <p className="text-[#555] text-[12px]">Configure geo-fencing from the Deploy page when creating a trap. Rules can filter by country, radius, or device type.</p>
      </P>}
    </div>
  );
}

// ═══════════════════════════════════════
// 4. THREAT INTEL PAGE
// ═══════════════════════════════════════
export function ThreatIntelPage() {
  const [summaries, setSummaries] = useState([]);
  const [anomalies, setAnomalies] = useState(null);
  const [tab, setTab] = useState('summaries');
  const [loading, setLoading] = useState(false);

  const loadSummaries = async () => {
    setLoading(true);
    try { setSummaries((await api.get('/api/v1/threat-intel/summary/batch')).data || []); } catch (e) { alert(E(e)); }
    setLoading(false);
  };
  const loadAnomalies = async () => {
    try { setAnomalies((await api.get('/api/v1/threat-intel/anomalies')).data); } catch (e) { alert(E(e)); }
  };

  useEffect(() => { loadSummaries(); loadAnomalies(); }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between"><div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">Threat Intelligence</p><h1 className="text-[22px] font-bold text-white">AI Threat Analysis</h1></div>
        <div className="flex border border-[#222] rounded-lg overflow-hidden">
          {[{ id: 'summaries', l: 'AI Summaries' }, { id: 'anomalies', l: `Anomalies${anomalies ? ` (${anomalies.total})` : ''}` }].map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-[11px] ${tab === t.id ? 'bg-white text-black' : 'text-[#666] hover:text-white'}`}>{t.l}</button>)}
        </div></div>

      {tab === 'summaries' && <P title="AI-Generated Threat Summaries" extra={<Bn v="ghost" onClick={loadSummaries}>{loading ? '...' : 'Refresh'}</Bn>}>
        {summaries.map(s => <div key={s.log_id} className="border border-[#1a1a1a] rounded-lg p-4 mb-3">
          <div className="flex justify-between mb-2"><span className="text-white font-mono text-[12px]">{s.ip}</span><span className="text-[10px] text-[#444]">{s.time}</span></div>
          <p className="text-[13px] text-[#ccc] leading-relaxed">{s.summary}</p>
        </div>)}
      </P>}

      {tab === 'anomalies' && anomalies && <div className="space-y-4">
        {anomalies.realtime?.length > 0 && <P title="Real-time Anomalies" extra={<Bd v="danger">{anomalies.realtime.length} detected</Bd>}>
          {anomalies.realtime.map((a, i) => <div key={i} className={`border rounded-lg p-3 mb-2 ${a.severity === 'high' ? 'border-red-900 bg-red-950/10' : 'border-amber-900 bg-amber-950/10'}`}>
            <div className="flex justify-between"><Bd v={a.severity === 'high' ? 'danger' : 'warning'}>{a.type}</Bd><Bd>{a.severity}</Bd></div>
            <p className="text-[12px] text-[#ccc] mt-2">{a.description}</p>
          </div>)}
        </P>}
        {anomalies.stored?.length > 0 && <P title="Stored Alerts">{anomalies.stored.map(a => <div key={a.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2 flex justify-between items-center">
          <div><Bd v="warning">{a.anomaly_type}</Bd><span className="text-[12px] text-[#ccc] ml-2">{a.description}</span></div>
          <button onClick={() => api.put(`/api/v1/threat-intel/anomalies/${a.id}/read`).catch(() => {})} className="text-[10px] text-[#555]">Dismiss</button>
        </div>)}</P>}
        {(!anomalies.realtime?.length && !anomalies.stored?.length) && <P title="Anomaly Detection"><p className="text-[#555] text-center py-8">No anomalies detected. The system continuously monitors for unusual patterns.</p></P>}
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════
// 5. DOMAIN MANAGER PAGE
// ═══════════════════════════════════════
export function DomainManagerPage() {
  const [domains, setDomains] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [tab, setTab] = useState('domains');
  const [newDomain, setNewDomain] = useState('');
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    api.get('/api/v1/domains').then(r => setDomains(r.data || [])).catch(() => {});
    api.get('/api/v1/access-tokens').then(r => setTokens(r.data || [])).catch(() => {});
  }, []);

  const addDomain = async () => {
    if (!newDomain) return;
    try { await api.post('/api/v1/domains', { domain: newDomain, label: newLabel }); setNewDomain(''); setNewLabel(''); api.get('/api/v1/domains').then(r => setDomains(r.data || [])); } catch (e) { alert(E(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between"><div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">Infrastructure</p><h1 className="text-[22px] font-bold text-white">Domain Manager</h1></div>
        <div className="flex border border-[#222] rounded-lg overflow-hidden">
          {[{ id: 'domains', l: 'Custom Domains' }, { id: 'tokens', l: 'Access Tokens' }, { id: 'anonymity', l: 'Operator Anonymity' }].map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-[11px] ${tab === t.id ? 'bg-white text-black' : 'text-[#666] hover:text-white'}`}>{t.l}</button>)}
        </div></div>

      {tab === 'domains' && <P title="Custom Domains" sub="Use your own domains to mask trap link identity">
        <div className="flex gap-2 mb-4">
          <input className="flex-1 bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" placeholder="secure-docs.com" value={newDomain} onChange={e => setNewDomain(e.target.value)} />
          <input className="w-[160px] bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" placeholder="Label" value={newLabel} onChange={e => setNewLabel(e.target.value)} />
          <Bn onClick={addDomain}>Add Domain</Bn>
        </div>
        {domains.map(d => <div key={d.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2 flex justify-between items-center">
          <div className="flex items-center gap-3"><span className="text-white font-mono text-[13px]">{d.domain}</span>{d.label && <span className="text-[11px] text-[#555]">{d.label}</span>}</div>
          <div className="flex gap-2">
            <Bd v={d.is_verified ? 'success' : 'warning'}>{d.is_verified ? 'Verified' : 'Pending'}</Bd>
            {!d.is_verified && <button onClick={() => api.get(`/api/v1/domains/${d.id}/verify`).then(() => api.get('/api/v1/domains').then(r => setDomains(r.data || []))).catch(e => alert(E(e)))} className="text-[10px] text-blue-400">Verify</button>}
            <button onClick={() => api.delete(`/api/v1/domains/${d.id}`).then(() => setDomains(p => p.filter(x => x.id !== d.id))).catch(() => {})} className="text-[10px] text-red-400">Remove</button>
          </div>
        </div>)}
      </P>}

      {tab === 'tokens' && <P title="Temporary Access Tokens" sub="Share specific data without creating accounts">
        {tokens.map(t => <div key={t.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2 flex justify-between items-center">
          <div><span className="text-white text-[13px]">{t.name || 'Unnamed'}</span><span className="text-[11px] text-[#555] ml-2">{t.scope} · {t.target_type}</span></div>
          <div className="flex gap-2"><Bd>{t.access_count} accesses</Bd><Bd v={t.is_active ? 'success' : 'danger'}>{t.is_active ? 'Active' : 'Revoked'}</Bd>
            {t.is_active && <button onClick={() => api.delete(`/api/v1/access-tokens/${t.id}`).then(() => api.get('/api/v1/access-tokens').then(r => setTokens(r.data || []))).catch(() => {})} className="text-[10px] text-red-400">Revoke</button>}
          </div>
        </div>)}
      </P>}

      {tab === 'anonymity' && <P title="Operator Anonymity" sub="How ZureX92 protects your identity">
        <div className="space-y-3 text-[13px] text-[#ccc]">
          <div className="border border-[#1a1a1a] rounded-lg p-3"><Bd v="success">Active</Bd><span className="ml-2">Response headers cleaned (x-vercel-id, x-powered-by, server)</span></div>
          <div className="border border-[#1a1a1a] rounded-lg p-3"><Bd v="success">Active</Bd><span className="ml-2">Trap HTML contains no platform references</span></div>
          <div className="border border-[#1a1a1a] rounded-lg p-3"><Bd v="blue">Custom Domains</Bd><span className="ml-2">Use custom domains to completely separate trap identity from platform</span></div>
          <div className="border border-[#1a1a1a] rounded-lg p-3"><Bd v="blue">Cloudflare</Bd><span className="ml-2">Route through Cloudflare proxy to hide origin server IP</span></div>
        </div>
      </P>}
    </div>
  );
}

// ═══════════════════════════════════════
// 6. WORKFLOW BUILDER PAGE
// ═══════════════════════════════════════
export function WorkflowPage() {
  const [workflows, setWorkflows] = useState([]);
  const [stepTypes, setStepTypes] = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/api/v1/automation/workflows').then(r => setWorkflows(r.data || [])).catch(() => {});
    api.get('/api/v1/automation/step-types').then(r => setStepTypes(r.data || {})).catch(() => {});
  }, []);

  return (
    <div className="space-y-5">
      <div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">Automation</p><h1 className="text-[22px] font-bold text-white">Workflow Builder</h1></div>
      <P title="Workflows" sub="Automated intelligence processing pipelines">
        {workflows.length === 0 ? <p className="text-[#555] text-center py-8">No workflows yet. Create your first automation.</p> :
          workflows.map(w => <div key={w.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2">
            <div className="flex justify-between items-center">
              <div><span className="text-white text-[14px] font-medium">{w.name}</span>{w.description && <p className="text-[11px] text-[#555] mt-0.5">{w.description}</p>}</div>
              <div className="flex gap-2"><Bd>{w.run_count} runs</Bd><Bd v={w.is_active ? 'success' : 'default'}>{w.is_active ? 'Active' : 'Off'}</Bd>
                <Bn v="ghost" onClick={() => api.post(`/api/v1/automation/workflows/${w.id}/run`, { context: {} }).then(r => alert(`Executed ${r.data.steps_executed} steps`)).catch(e => alert(E(e)))}>Run</Bn>
              </div>
            </div>
            {w.steps && <div className="flex gap-1 mt-2 flex-wrap">{(typeof w.steps === 'string' ? JSON.parse(w.steps) : w.steps).map((s, i) => <span key={i} className="text-[9px] px-2 py-0.5 rounded bg-[#111] text-[#888] border border-[#222]">{s.type}</span>)}</div>}
          </div>)}
      </P>
      <P title="Available Step Types"><div className="grid grid-cols-2 gap-2">{Object.entries(stepTypes).map(([k, v]) => <div key={k} className="border border-[#1a1a1a] rounded-lg p-2 text-[12px]"><span className="text-white font-mono">{k}</span><p className="text-[10px] text-[#555] mt-0.5">{v}</p></div>)}</div></P>
    </div>
  );
}

// ═══════════════════════════════════════
// 7. INTEGRATIONS PAGE
// ═══════════════════════════════════════
export function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [services, setServices] = useState({});

  useEffect(() => {
    api.get('/api/v1/integrations').then(r => setIntegrations(r.data || [])).catch(() => {});
    api.get('/api/v1/integrations/services').then(r => setServices(r.data || {})).catch(() => {});
  }, []);

  return (
    <div className="space-y-5">
      <div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">External</p><h1 className="text-[22px] font-bold text-white">API Integrations</h1></div>
      <P title="Connected Services">{integrations.length === 0 ? <p className="text-[#555]">No integrations configured</p> : integrations.map(i => <div key={i.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2 flex justify-between items-center">
        <div><span className="text-white text-[13px] font-medium">{i.service_info?.name || i.service_name}</span><span className="text-[11px] text-[#555] ml-2">{i.usage_count} queries</span></div>
        <div className="flex gap-2"><Bd v={i.is_active ? 'success' : 'default'}>{i.is_active ? 'Connected' : 'Off'}</Bd>
          <button onClick={() => api.delete(`/api/v1/integrations/${i.id}`).then(() => setIntegrations(p => p.filter(x => x.id !== i.id))).catch(() => {})} className="text-[10px] text-red-400">Remove</button>
        </div></div>)}</P>
      <P title="Available Services" sub="Connect external APIs for enriched intelligence">
        <div className="grid grid-cols-2 gap-3">{Object.entries(services).map(([k, v]) => <div key={k} className="border border-[#1a1a1a] rounded-lg p-3">
          <div className="flex justify-between items-center mb-1"><span className="text-white text-[13px] font-medium">{v.name}</span><Bd v="blue">{k}</Bd></div>
          <p className="text-[11px] text-[#555]">{v.description}</p>
        </div>)}</div>
      </P>
    </div>
  );
}

// ═══════════════════════════════════════
// 8. CASE BUILDER PAGE
// ═══════════════════════════════════════
export function CaseBuilderPage() {
  const [reports, setReports] = useState([]);
  useEffect(() => { api.get('/api/v1/cases/reports').then(r => setReports(r.data || [])).catch(() => {}); }, []);

  return (
    <div className="space-y-5">
      <div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">Reports</p><h1 className="text-[22px] font-bold text-white">Case Builder & Evidence Chain</h1></div>
      <P title="Generated Reports" sub="Auto-built investigation reports">{reports.length === 0 ? <p className="text-[#555] text-center py-8">No reports generated. Use the 'Build Report' button in any Investigation to auto-generate.</p> :
        reports.map(r => <div key={r.id} className="border border-[#1a1a1a] rounded-lg p-3 mb-2">
          <div className="flex justify-between items-center"><span className="text-white text-[14px] font-medium">{r.title}</span>
            <div className="flex gap-2"><Bd>{r.status}</Bd>
              <Bn v="ghost" onClick={() => window.open(`${API}/api/v1/cases/present/${r.id}`, '_blank')}>Present</Bn>
            </div></div>
          <p className="text-[12px] text-[#555] mt-1">{r.executive_summary?.substring(0, 200)}...</p>
        </div>)}
      </P>
    </div>
  );
}

// ═══════════════════════════════════════
// 9. ADVANCED OSINT PAGE
// ═══════════════════════════════════════
export function AdvancedOsintPage() {
  const [tab, setTab] = useState('domain');
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monitors, setMonitors] = useState([]);

  useEffect(() => { api.get('/api/v1/adv-osint/darkweb/monitors').then(r => setMonitors(r.data || [])).catch(() => {}); }, []);

  const search = async () => {
    if (!query) return;
    setLoading(true); setResult(null);
    try {
      if (tab === 'domain') setResult((await api.post('/api/v1/adv-osint/domain-recon', { domain: query })).data);
      else if (tab === 'phone') setResult((await api.post('/api/v1/adv-osint/phone-intel', { phone: query })).data);
      else if (tab === 'imint') setResult((await api.post('/api/v1/adv-osint/imint/analyze', { url: query })).data);
    } catch (e) { alert(E(e)); }
    setLoading(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between"><div><p className="text-[11px] uppercase tracking-[0.22em] text-[#555]">OSINT+</p><h1 className="text-[22px] font-bold text-white">Advanced OSINT</h1></div>
        <div className="flex border border-[#222] rounded-lg overflow-hidden flex-wrap">
          {[{ id: 'domain', l: 'Domain Recon' }, { id: 'phone', l: 'Phone Intel' }, { id: 'imint', l: 'Image Intel' }, { id: 'darkweb', l: 'Dark Web' }].map(t => <button key={t.id} onClick={() => { setTab(t.id); setResult(null); }} className={`px-3 py-1.5 text-[11px] ${tab === t.id ? 'bg-white text-black' : 'text-[#666] hover:text-white'}`}>{t.l}</button>)}
        </div></div>

      {tab !== 'darkweb' && <P title={tab === 'domain' ? 'Domain Reconnaissance' : tab === 'phone' ? 'Phone Intelligence' : 'Image Intelligence (IMINT)'}>
        <div className="flex gap-3 mb-4">
          <input className="flex-1 bg-[#0a0a0a] border border-[#222] px-4 py-2.5 text-white rounded-lg text-[13px] outline-none font-mono"
            placeholder={tab === 'domain' ? 'example.com' : tab === 'phone' ? '+962...' : 'https://image-url.com/photo.jpg'}
            value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} />
          <Bn onClick={search} disabled={loading}>{loading ? 'Analyzing...' : 'Analyze'}</Bn>
        </div>
      </P>}

      {result && tab === 'domain' && <P title={`Domain: ${result.domain}`}>
        {result.dns && Object.keys(result.dns).length > 0 && <div className="mb-4"><h4 className="text-[12px] text-[#555] uppercase mb-2">DNS Records</h4>
          {Object.entries(result.dns).map(([t, records]) => <div key={t} className="mb-2"><Bd v="blue">{t}</Bd>{records?.map((r, i) => <span key={i} className="text-[12px] text-[#ccc] font-mono ml-2">{r}</span>)}</div>)}</div>}
        {result.subdomains?.length > 0 && <div className="mb-4"><h4 className="text-[12px] text-[#555] uppercase mb-2">Subdomains ({result.subdomains.length})</h4>
          <div className="flex flex-wrap gap-1">{result.subdomains.map(s => <span key={s} className="text-[11px] font-mono px-2 py-0.5 rounded bg-[#0a0a0a] border border-[#222] text-white">{s}</span>)}</div></div>}
        {result.tech?.length > 0 && <div><h4 className="text-[12px] text-[#555] uppercase mb-2">Technologies</h4>
          <div className="flex flex-wrap gap-1">{result.tech.map((t, i) => <Bd key={i} v="purple">{t}</Bd>)}</div></div>}
      </P>}

      {result && tab === 'phone' && <P title={`Phone: ${result.phone}`}>
        <div className="grid grid-cols-4 gap-3"><SC l="Country" v={result.country || '—'} /><SC l="Carrier" v={result.carrier || '—'} /><SC l="Type" v={result.type} /><SC l="Valid" v={result.valid ? 'Yes' : 'No'} /></div>
        {result.lookup_urls && <div className="mt-4 space-y-1">{Object.entries(result.lookup_urls).map(([k, v]) => <a key={k} href={v} target="_blank" rel="noopener" className="block text-[12px] text-blue-400 hover:underline">{k}: {v}</a>)}</div>}
      </P>}

      {result && tab === 'imint' && <P title="Image Analysis">
        <div className="grid grid-cols-3 gap-3 mb-4"><SC l="Size" v={result.metadata?.size || '—'} /><SC l="Format" v={result.metadata?.format || '—'} /><SC l="File Size" v={result.metadata?.size_bytes ? `${Math.round(result.metadata.size_bytes / 1024)}KB` : '—'} /></div>
        {result.hashes && <div className="mb-4"><h4 className="text-[12px] text-[#555] uppercase mb-2">Hashes</h4>
          <div className="text-[11px] font-mono text-[#888]"><p>MD5: {result.hashes.md5}</p><p>SHA256: {result.hashes.sha256}</p></div></div>}
        {result.exif && Object.keys(result.exif).length > 0 && <div className="mb-4"><h4 className="text-[12px] text-[#555] uppercase mb-2">EXIF Data</h4>
          {Object.entries(result.exif).slice(0, 20).map(([k, v]) => <div key={k} className="flex justify-between py-1 border-b border-[#0a0a0a] text-[11px]"><span className="text-[#555]">{k}</span><span className="text-[#ccc]">{typeof v === 'object' ? JSON.stringify(v) : String(v).substring(0, 60)}</span></div>)}</div>}
        {result.analysis?.gps_location && <div><h4 className="text-[12px] text-[#555] uppercase mb-2">GPS Location Found!</h4>
          <a href={result.analysis.maps_url} target="_blank" rel="noopener" className="text-blue-400 text-[13px]">Open in Google Maps →</a></div>}
      </P>}

      {tab === 'darkweb' && <P title="Dark Web Monitor" sub="Monitor for leaked credentials and mentions">
        <div className="space-y-2">{monitors.map(m => <div key={m.id} className="border border-[#1a1a1a] rounded-lg p-3 flex justify-between items-center">
          <div><Bd v="purple">{m.query_type}</Bd><span className="text-white text-[13px] ml-2 font-mono">{m.query_value}</span></div>
          <div className="flex gap-2"><Bd>{m.alert_count} alerts</Bd>
            <Bn v="ghost" onClick={() => api.post(`/api/v1/adv-osint/darkweb/check/${m.id}`).then(r => alert(`${r.data.results?.length || 0} sources checked`)).catch(e => alert(E(e)))}>Check</Bn>
            <button onClick={() => api.delete(`/api/v1/adv-osint/darkweb/monitors/${m.id}`).then(() => setMonitors(p => p.filter(x => x.id !== m.id))).catch(() => {})} className="text-[10px] text-red-400">×</button>
          </div>
        </div>)}</div>
      </P>}
    </div>
  );
}
